/**
 * PanelContextBanner.tsx
 *
 * Persistent banner shown at the top of the AI chat panel when the user
 * has clicked "Edit with AI" on a panel and entered panel-scoped edit mode
 * (mockup 21, brand guide §32).
 *
 * Shows:
 *  - Pencil icon in an accent-bg rounded box
 *  - "EDITING PANEL" label (10px, bold, uppercase, accent)
 *  - Panel name (12.5px, bold, truncated)
 *  - "Exit edit" button — dispatches exitEditMode action
 *
 * Accessibility:
 *  - role="status" aria-label="Editing panel: <panelTitle>" on the container.
 *  - "Exit edit" button has descriptive aria-label.
 *  - focus-visible:ring-2 focus-visible:ring-focusRing on the exit button.
 */

import React from 'react'
import { Pencil } from 'lucide-react'
import { useDispatch } from 'react-redux'
import type { AppDispatch } from '@/redux'
import { dashboardViewActions } from '@/redux/slice/dashboardViewSlice'
import { Button } from '@/components/ui/button'

interface PanelContextBannerProps {
  panelTitle: string
}

export const PanelContextBanner: React.FC<PanelContextBannerProps> = ({ panelTitle }) => {
  const dispatch: AppDispatch = useDispatch()

  const handleExit = () => {
    dispatch(dashboardViewActions.exitEditMode())
  }

  return (
    <div
      className="flex items-center gap-2 px-3.5 py-2 bg-accent-background border-b border-accent/30 flex-shrink-0"
      role="status"
      aria-label={`Editing panel: ${panelTitle}`}
    >
      {/* Pencil icon in accent-bg square (brand guide §32) */}
      <div
        className="w-6 h-6 rounded bg-accent flex items-center justify-center flex-shrink-0"
        aria-hidden="true"
      >
        <Pencil size={11} className="text-accent-foreground" aria-hidden="true" />
      </div>

      {/* Panel identity */}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-wider text-accent mb-0.5">
          Editing panel
        </p>
        <p className="text-[12.5px] font-semibold text-primary truncate">{panelTitle}</p>
      </div>

      {/* Exit edit mode — use Button primitive for consistent focus-visible
          handling; link variant gives us the text-link semantics and we override
          color/underline per the §32 spec (accent text, underline on hover only). */}
      <Button
        type="button"
        variant="link"
        className="h-auto p-0 text-xs font-semibold text-accent no-underline hover:underline hover:text-accent flex-shrink-0 rounded-sm"
        aria-label="Exit panel edit mode — return to dashboard chat"
        onClick={handleExit}
      >
        Exit edit
      </Button>
    </div>
  )
}
